import { z } from "zod";
export const notificationTypeSchema = z.enum(["integration_sync_failed"]);
export const notificationSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    type: notificationTypeSchema,
    title: z.string(),
    body: z.string().nullable(),
    metadata: z.unknown().nullable(),
    isRead: z.boolean(),
    readAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
});
const cursorIdSchema = z.string().uuid();
/**
 * Opaque keyset-pagination cursor: "<createdAt ISO>|<id>". Both fields are
 * required, not just createdAt: a single notifyOrgAdmins() call bulk-inserts
 * one row per admin in one statement, and Postgres's now() default evaluates
 * once per statement — so every recipient's row in that batch shares the
 * exact same createdAt down to the microsecond. createdAt alone can't
 * unambiguously order or paginate across those ties (and a JS Date only
 * has millisecond precision to begin with, so even "equal" vs. "very close"
 * isn't reliable). id breaks the tie and gives every row an unambiguous
 * position; see notifications.repository.ts's compound ORDER BY/WHERE.
 */
export const notificationCursorSchema = z.string().transform((val, ctx) => {
    const [createdAtRaw, id] = val.split("|");
    const createdAt = createdAtRaw ? new Date(createdAtRaw) : null;
    const idIsValid = !!id && cursorIdSchema.safeParse(id).success;
    if (!createdAtRaw || !id || !createdAt || Number.isNaN(createdAt.getTime()) || !idIsValid) {
        ctx.addIssue({ code: "custom", message: "Invalid cursor" });
        return z.NEVER;
    }
    return { createdAt, id };
});
export const listNotificationsQuerySchema = z.object({
    cursor: notificationCursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    unreadOnly: z
        .enum(["true", "false"])
        .transform((v) => v === "true")
        .optional(),
});
export const listNotificationsResponseSchema = z.object({
    notifications: z.array(notificationSchema),
    // Same "<createdAt ISO>|<id>" shape as notificationCursorSchema decodes.
    nextCursor: z.string().nullable(),
});
export const unreadCountResponseSchema = z.object({
    count: z.number().int(),
});
export const markReadResponseSchema = z.object({
    success: z.boolean(),
});
export const markAllReadResponseSchema = z.object({
    success: z.boolean(),
    count: z.number().int(),
});
export const testNotificationResponseSchema = z.object({
    success: z.boolean(),
});
